import api from '../lib/axios';
import { AxiosError } from 'axios';
import { logger } from '../utils/logger';
import { tokenStorage } from '../utils/tokenStorage';
import type {
  LoginRequest,
  RegisterRequest,
  RefreshTokenRequest,
  SendOTPRequest,
  VerifyOTPRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthResponse,
  OTPResponse,
  RefreshTokenResponse,
  ProfileResponse,
} from '../types/auth';

// API Endpoints from environment variables
const API_ENDPOINTS = {
  LOGIN: import.meta.env.VITE_API_V1_AUTH_LOGIN,
  REGISTER: import.meta.env.VITE_API_V1_AUTH_REGISTER,
  LOGOUT: import.meta.env.VITE_API_V1_AUTH_LOGOUT,
  VERIFY_EMAIL_SEND: import.meta.env.VITE_API_V1_AUTH_VERIFY_EMAIL_SEND,
  VERIFY_PHONE_SEND: import.meta.env.VITE_API_V1_AUTH_VERIFY_PHONE_SEND,
  VERIFY_OTP: import.meta.env.VITE_API_V1_AUTH_VERIFY_OTP,
  PASSWORD_RESET_REQUEST: import.meta.env.VITE_API_V1_AUTH_PASSWORD_RESET_REQUEST,
  PASSWORD_RESET_CONFIRM: import.meta.env.VITE_API_V1_AUTH_PASSWORD_RESET_CONFIRM,
  USERS_PROFILE: import.meta.env.VITE_API_V1_USERS_PROFILE,
};

const authService = {
  // Login user
  login: async (credentials: LoginRequest): Promise<AuthResponse> => {
    try {
      logger.log('Login request:', credentials);
      const response = await api.post<AuthResponse>(API_ENDPOINTS.LOGIN, credentials);
      logger.log('Login response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Login error:', axiosError);
      logger.error('Login error response:', axiosError.response?.data);
      return {
        success: false,
        error:
          axiosError.response?.data?.error || axiosError.response?.data?.message || 'Login failed',
      };
    }
  },

  // Register new user
  register: async (userData: RegisterRequest): Promise<AuthResponse> => {
    try {
      const response = await api.post<AuthResponse>(API_ENDPOINTS.REGISTER, userData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Registration error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Registration failed',
      };
    }
  },

  // Refresh access token
  refreshToken: async (refreshData: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
    // Skip refresh if offline
    if (!navigator.onLine) {
      logger.log('Offline: Skipping token refresh');
      // Return a mock success response to keep the session "alive" in the app state
      // This assumes the token is still valid or we don't care until back online
      return {
        success: true,
        accessToken: tokenStorage.getAccessToken() || '',
        refreshToken: tokenStorage.getRefreshToken() || '',
      };
    }

    try {
      logger.log('AuthService: Attempting token refresh...');
      const response = await api.post<RefreshTokenResponse>(
        '/api/v1/auth/refresh-token',
        refreshData,
      );
      logger.log('AuthService: Token refresh response:', response.data);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('AuthService: Token refresh error:', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        message: axiosError.message,
      });

      // Handle specific error cases
      if (axiosError.response?.status === 401) {
        // Throw error for 401 so AuthProvider can detect and logout immediately
        throw new Error('401: Refresh token expired or invalid');
      }

      if (axiosError.response?.status === 403) {
        return {
          success: false,
          error: 'Refresh token access denied',
        };
      }

      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Token refresh failed',
      };
    }
  },

  // Send OTP for phone or email verification
  sendOTP: async (otpData: SendOTPRequest): Promise<OTPResponse> => {
    try {
      // Determine endpoint based on whether email or phone is provided
      const endpoint = otpData.email
        ? API_ENDPOINTS.VERIFY_EMAIL_SEND
        : API_ENDPOINTS.VERIFY_PHONE_SEND;

      const response = await api.post<OTPResponse>(endpoint, otpData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Send OTP error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Failed to send OTP',
      };
    }
  },

  // Verify SMS OTP
  verifyOTP: async (otpData: VerifyOTPRequest): Promise<OTPResponse> => {
    try {
      const response = await api.post<OTPResponse>(API_ENDPOINTS.VERIFY_OTP, otpData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Verify OTP error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'OTP verification failed',
      };
    }
  },

  // Logout user
  logout: async (): Promise<{ success: boolean; error?: string }> => {
    try {
      await api.post(API_ENDPOINTS.LOGOUT);
      return { success: true };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Logout error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Logout failed',
      };
    }
  },

  // Request password reset
  forgotPassword: async (resetData: ForgotPasswordRequest): Promise<OTPResponse> => {
    try {
      const response = await api.post<OTPResponse>(API_ENDPOINTS.PASSWORD_RESET_REQUEST, resetData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Forgot password error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Password reset request failed',
      };
    }
  },

  // Confirm password reset with OTP
  resetPassword: async (resetData: ResetPasswordRequest): Promise<OTPResponse> => {
    try {
      const response = await api.post<OTPResponse>(API_ENDPOINTS.PASSWORD_RESET_CONFIRM, resetData);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Reset password error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Password reset failed',
      };
    }
  },

  // Get user profile
  getUserProfile: async (): Promise<ProfileResponse> => {
    // Check offline first
    if (!navigator.onLine) {
      const offlineProfile = localStorage.getItem('offline_user_profile');
      if (offlineProfile) {
        logger.log('Returning offline user profile');
        return {
          success: true,
          data: JSON.parse(offlineProfile),
        };
      }
    }

    try {
      const response = await api.get(API_ENDPOINTS.USERS_PROFILE);

      // Cache profile for offline use
      if (response.data.user) {
        localStorage.setItem('offline_user_profile', JSON.stringify(response.data.user));
      }

      return {
        success: true,
        data: response.data.user,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string }>;
      logger.error('Get user profile error:', axiosError);
      return {
        success: false,
        error: axiosError.response?.data?.error || 'Failed to fetch user profile',
      };
    }
  },

  // Switch active role and get new tokens
  switchRole: async (role?: 'user' | 'merchant'): Promise<RefreshTokenResponse> => {
    try {
      // Some backends toggle role without a body; send role if provided
      const payload = role ? { role } : {};
      const response = await api.post<RefreshTokenResponse>('/api/v1/auth/switch-role', payload);
      return response.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ error?: string; message?: string }>;
      logger.error('Switch role error:', {
        status: axiosError.response?.status,
        data: axiosError.response?.data,
        message: axiosError.message,
      });
      return {
        success: false,
        error:
          axiosError.response?.data?.error ||
          axiosError.response?.data?.message ||
          'Role switch failed',
      };
    }
  },
};

export default authService;
